import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useMemo } from "react"
import TabsList from "../../../components/Common/TabsList"
import { handleError, userHasPermission } from "../../../utils"
import { NoPermission } from "../../../components/UI/no-permission"
import { readCategories } from "../../../api/categories"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
    Box,
    Button,
    Container,
    createListCollection,
    Flex,
    HStack,
    Icon,
    Text
} from "@chakra-ui/react"
import useCustomToast from "../../../hooks/useCustomToast"
import { readSettingsLandingpage } from "../../../api/landingpage"
import { ApiError, SettingsLandingpageService, SettingsLandingpageUpdateRequest } from "../../../client"
import { SubmitHandler, useFieldArray, useForm } from "react-hook-form"
import LandingpageCategories from "../../../components/Landingpage/LandingpageCategories"
import LandingpageRubrics from "../../../components/Landingpage/LandingpageRubrics"
import { Tooltip } from "../../../components/UI/tooltip"
import { FiInfo } from "react-icons/fi"

export const Route = createFileRoute("/_layout/settings/landingpage")({
  component: Landingpage,
})


function LandingpageTable() {
    // Permission check
    const [lp_read, lp_update, ct_read] = useMemo(() => {
        const [lp_read, lp_update] = ["read", "update"].map((action) => userHasPermission([{ perm: `settings_landingpage_${action}`, is_allowed: false }])[0].is_allowed)
        const [ct_read] = ["read"].map((action) => userHasPermission([{ perm: `categories_${action}`, is_allowed: false }])[0].is_allowed)

        return [lp_read, lp_update, ct_read]
    }, [userHasPermission])

    if(!lp_read) return <NoPermission/>

    // Get all categories
    const categoriesQuery = useMemo(() => readCategories({per_page: 0, page: 0, showInactive: false}), [])
    const { data: categoriesList } = useQuery(categoriesQuery)

    // Create category collection from all categories
    const categoryCollection = createListCollection({
        items: categoriesList?.data?.map(c => ({
            value: c.id,
            label: c.name,
        }))|| [],
    })

    // Query
    const queryClient = useQueryClient()
    const showToast = useCustomToast()

    const {data: landingpagesettings} = useQuery({
        ...readSettingsLandingpage(),
    })

    //Form default values
    const defaultValues = useMemo(() => ({
        rubrics: {
            landingpage_rubrics: (landingpagesettings?.rubrics?.data ?? []).map(item => ({
                fk_category_id: item.fk_category.id,
            }))
        },
        categories: {
            landingpage_categories: (landingpagesettings?.categories?.data ?? []).map((item) => ({
                id: item.id,
                title: item.title,
                slot_number: item.slot_number,
                is_active: item.is_active,
                publication_display_amount: item.publication_display_amount,
                fk_category_id: item.fk_category.id,
            })),
        },
    }), [landingpagesettings])

    // Form
    const form = useForm<SettingsLandingpageUpdateRequest>({
        mode: "onBlur",
        criteriaMode: "all",
        defaultValues: defaultValues,
    })

    const {
        handleSubmit,
        reset,
        watch,
        control,
        formState: {isSubmitting, isDirty},
    } = form

    const catArray = useFieldArray({
        control,
        name: "categories.landingpage_categories"
    })

    const categories = watch("categories.landingpage_categories") ?? []

    useEffect(() => {
        if (!landingpagesettings) return
        reset(defaultValues)
    }, [defaultValues, reset])


    // Mutate form data
    const mutation = useMutation({
        mutationFn: (data: SettingsLandingpageUpdateRequest) =>
            SettingsLandingpageService.updateSettingsLandingpage({ requestBody: data }),
        onSuccess: () => {
            showToast("Aktion erfolgreich!", "Startseiteneinstellungen wurden angepasst", "success")
            reset()
        },
        onError: (err: ApiError) => {
            handleError(err, showToast)
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ["settings-landingpage"] })
        },
    })

    // Submit form
    const onSubmit: SubmitHandler<SettingsLandingpageUpdateRequest> = async (data) => {
        mutation.mutate(data)
    }

    // Cancle form
    const onCancel = () => {
        reset(defaultValues)
    }

    return (
        <Container maxW="full" my={10}>
            <Box
                w={{ sm: "full", md: "50%" }}
                as="form"
                onSubmit={handleSubmit(onSubmit)}
            >
                <HStack pb={4}>
                    Kategorien
                    <Tooltip
                        showArrow
                        content="Eine Kategorie zeigt die letzten Beiträge der angegebenen Kategorie an"
                        openDelay={500}
                    >
                        <Icon as={FiInfo} data-testid="info_lp_categories" />
                    </Tooltip>
                </HStack>

                <LandingpageCategories
                    form={form}
                    fieldArray={catArray}
                    categoryBlocks={categories}
                    categoryList={categoriesList}
                    categoryCollection={categoryCollection}
                    ct_read={ct_read}
                />

                <HStack pt={10}>
                    Rubriken
                    <Tooltip
                        showArrow
                        content="Eine Rubrik zeigt pro angegebene Kategorie den jeweils letzten Beitrag an"
                        openDelay={500}
                    >
                        <Icon as={FiInfo} data-testid="info_lp_rubrics" />
                    </Tooltip>
                </HStack>

                {ct_read ? (
                    <LandingpageRubrics form={form} rubrics={landingpagesettings?.rubrics?.data} categoryList={categoriesList}/>
                ) : (
                    <Text color="danger">Keine Berechtigung für Kategorien</Text>
                )}

                <Flex mt={4} mb={8} gap={8}>
                    <Button
                        variant="primary"
                        type="submit"
                        loading={isSubmitting}
                        disabled={!isDirty || !lp_update }
                    >
                        Speichern
                    </Button>

                    <Button
                        onClick={onCancel}
                        disabled={isSubmitting || !isDirty}
                        variant="secondary"
                    >
                        Abbrechen
                    </Button>
                </Flex>
            </Box>
        </Container>
    )
}

export default LandingpageTable

function Landingpage() {
    const tabs = useMemo(() => [
        { value: "settings", title: "Allgemein" },
        { value: "settings/navigation", title: "Navigation" },
        { value: "settings/landingpage", title: "Startseite" },
        { value: "settings/mimetypes", title: "Dateitypen" },
        { value: "settings/subscriptions", title: "Abos" },
        { value: "settings/priocategories", title: "Kategorien" },
        { value: "settings/ratecharts", title: "Tariftabellen" },
        { value: "settings/ratecharts", title: "Aktionen" },
    ], [])

    return <TabsList tabs={tabs} defaultVal="settings" />
}
